  import React, { useState, useEffect } from 'react';
  import { useSelector } from 'react-redux';
  import { toast } from 'react-hot-toast';
  import {
    useGetInstructorProfileQuery,
    useUpdateInstructorProfileMutation,
    useGetMyCoursesQuery,
  } from '../../api/apiSlice';
  import {
    Loader,
    Save,
    CheckCircle,
    AlertTriangle,
    BookOpen,
    Plus,
    Trash2,
    Briefcase,
    GraduationCap,
    Award,
    Link2,
    Wallet,
    UserCircle2,
    Sparkles,
    ShieldCheck,
    LayoutDashboard,
    Tag,
    ClipboardCheck,
    GraduationCap as Cap,
    TrendingUp,
    Copy,
  } from 'lucide-react';
  import { Link } from 'react-router-dom';
  import AvatarUpload from '../../components/profile/AvatarUpload';

  const ProfilePage = () => {
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.roles?.includes('admin');
    const isInstructor = user?.roles?.includes('instructor');
    const isStudent = !isAdmin && !isInstructor;
    const userId = user?._id || user?.id || '';

const copyUserId = async () => {
  if (!userId) {
    toast.error('ID пользователя не найден');
    return;
  }

  try {
    await navigator.clipboard.writeText(userId);
    toast.success('ID скопирован');
  } catch {
    toast.error('Не удалось скопировать ID');
  }
};

    const { data: profileResponse, isLoading, refetch } = useGetInstructorProfileQuery(undefined, {
      skip: !isInstructor,
    });

    // Лёгкая статистика для студента: количество курсов и средний прогресс.
    const { data: myCourses = [] } = useGetMyCoursesQuery(undefined, { skip: !isStudent });
    const studentStats = (() => {
      if (!isStudent) return null;
      const total = myCourses.length;
      const completed = myCourses.filter((e) => (e.progress || 0) >= 100).length;
      const avg = total
        ? Math.round(myCourses.reduce((s, e) => s + (e.progress || 0), 0) / total)
        : 0;
      return { total, completed, avg };
    })();

    const profile = profileResponse?.profile;

    const [updateProfile, { isLoading: isUpdating, isSuccess, error: updateError }] =
        useUpdateInstructorProfileMutation();

    const [formData, setFormData] = useState({
      publicName: '',
      specialization: '',
      bio: '',
      experience: 0,
      kaspiPhone: '',
      socialLinks: {
        telegram: '',
        linkedin: '',
        github: '',
        instagram: '',
        website: '',
      },
      education: [],
      workExperience: [],
      achievements: [],
    });

    const [newEducation, setNewEducation] = useState({
      institution: '',
      degree: '',
      field: '',
      year: '',
    });

    const [newWork, setNewWork] = useState({
      position: '',
      company: '',
      startYear: '',
      endYear: '',
      current: false,
      description: '',
    });

    const [newAchievement, setNewAchievement] = useState({
      title: '',
      year: '',
      description: '',
      issuer: '',
    });

    useEffect(() => {
      if (profile) {
        setFormData({
          publicName: profile.publicName || '',
          specialization: profile.specialization || '',
          bio: profile.bio || '',
          experience: profile.experience || 0,
          kaspiPhone: profile.paymentDetails?.kaspi?.phone || '',
          socialLinks: profile.socialLinks || {
            telegram: '',
            linkedin: '',
            github: '',
            instagram: '',
            website: '',
          },
          education: profile.education || [],
          workExperience: profile.workExperience || [],
          achievements: profile.achievements || [],
        });
      }
    }, [profile]);

    const inputClass =
        'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/70 px-4 py-3 text-gray-900 dark:text-white shadow-sm outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10';

    const textareaClass = `${inputClass} min-h-[110px] resize-none`;

    const sectionCardClass =
        'rounded-2xl border border-gray-200/70 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm';

    const softCardClass =
        'rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-700/40 p-4';

    const iconTitleClass =
        'text-xl font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2';

    const itemCardClass =
        'flex justify-between items-start gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/60 p-4 shadow-sm';

    const removeBtnClass =
        'inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition';

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSocialLinkChange = (platform, value) => {
      setFormData((prev) => ({
        ...prev,
        socialLinks: { ...prev.socialLinks, [platform]: value },
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      const payload = {
        publicName: formData.publicName,
        specialization: formData.specialization,
        bio: formData.bio,
        experience: Number(formData.experience),
        socialLinks: formData.socialLinks,
        education: formData.education.map((edu) => ({
          institution: edu.institution,
          degree: edu.degree,
          field: edu.field,
          year: Number(edu.year) || null,
        })),
        workExperience: formData.workExperience.map((work) => ({
          position: work.position,
          company: work.company,
          startYear: Number(work.startYear) || null,
          endYear: work.current ? null : Number(work.endYear) || null,
          current: work.current || false,
          description: work.description || '',
        })),
        achievements: formData.achievements.map((ach) => ({
          title: ach.title,
          year: Number(ach.year) || null,
          description: ach.description || '',
          issuer: ach.issuer || '',
        })),
        paymentDetails: {
          kaspi: {
            phone: formData.kaspiPhone,
          },
        },
      };

      const toastId = toast.loading('Сохранение профиля...');

      try {
        await updateProfile(payload).unwrap();
        toast.success('Профиль успешно сохранён', { id: toastId });
        setTimeout(() => refetch(), 700);
      } catch (error) {
        toast.error(error?.data?.message || 'Ошибка сохранения профиля', {
          id: toastId,
        });
        console.error('Ошибка сохранения профиля:', error);
      }
    };

    const addEducation = () => {
      if (!newEducation.institution || !newEducation.degree) {
        toast.error('Заполните обязательные поля образования');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        education: [
          ...prev.education,
          {
            institution: newEducation.institution,
            degree: newEducation.degree,
            field: newEducation.field,
            year: newEducation.year ? Number(newEducation.year) : null,
          },
        ],
      }));

      toast.success('Образование добавлено');
      setNewEducation({ institution: '', degree: '', field: '', year: '' });
    };

    const removeEducation = (index) => {
      setFormData((prev) => ({
        ...prev,
        education: prev.education.filter((_, i) => i !== index),
      }));
      toast.success('Образование удалено');
    };

    const addWorkExperience = () => {
      if (!newWork.position || !newWork.company) {
        toast.error('Заполните должность и компанию');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        workExperience: [
          ...prev.workExperience,
          {
            position: newWork.position,
            company: newWork.company,
            startYear: newWork.startYear ? Number(newWork.startYear) : null,
            endYear: newWork.current ? null : newWork.endYear ? Number(newWork.endYear) : null,
            current: newWork.current,
            description: newWork.description,
          },
        ],
      }));

      toast.success('Опыт работы добавлен');
      setNewWork({
        position: '',
        company: '',
        startYear: '',
        endYear: '',
        current: false,
        description: '',
      });
    };

    const removeWorkExperience = (index) => {
      setFormData((prev) => ({
        ...prev,
        workExperience: prev.workExperience.filter((_, i) => i !== index),
      }));
      toast.success('Опыт работы удалён');
    };

    const addAchievement = () => {
      if (!newAchievement.title) {
        toast.error('Введите название достижения');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        achievements: [
          ...prev.achievements,
          {
            title: newAchievement.title,
            year: newAchievement.year ? Number(newAchievement.year) : null,
            description: newAchievement.description,
            issuer: newAchievement.issuer,
          },
        ],
      }));

      toast.success('Достижение добавлено');
      setNewAchievement({ title: '', year: '', description: '', issuer: '' });
    };

    const removeAchievement = (index) => {
      setFormData((prev) => ({
        ...prev,
        achievements: prev.achievements.filter((_, i) => i !== index),
      }));
      toast.success('Достижение удалено');
    };

    const calculateCompletion = () => {
      let completed = 0;
      if (formData.publicName?.trim()) completed += 15;
      if (formData.specialization?.trim()) completed += 15;
      if (formData.bio && formData.bio.length > 50) completed += 15;
      if (formData.experience > 0) completed += 10;
      if (formData.education.length > 0) completed += 10;
      if (formData.workExperience.length > 0) completed += 10;
      if (Object.values(formData.socialLinks).some((link) => link?.trim())) completed += 10;
      if (formData.kaspiPhone?.trim()) completed += 5;
      if (formData.achievements.length > 0) completed += 10;
      return Math.min(completed, 100);
    };

    const completionPercentage = calculateCompletion();

    const socialFields = [
      { key: 'telegram', label: 'Telegram', placeholder: '@username' },
      { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/username' },
      { key: 'github', label: 'GitHub', placeholder: 'github.com/username' },
      { key: 'instagram', label: 'Instagram', placeholder: '@username' },
      { key: 'website', label: 'Сайт', placeholder: 'example.com' },
    ];

    const studentContent = (
        <div className={`${sectionCardClass} overflow-hidden`}>
          <div className="border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 p-8">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-gray-700 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 shadow-sm">
              <Cap size={16} />
              Профиль студента
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{user?.name || '—'}</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{user?.email || '—'}</p>
            <div className="mt-4 rounded-xl border border-indigo-200 bg-white/80 p-4 dark:border-indigo-900/50 dark:bg-gray-900/50">
  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
    ID пользователя
  </div>

  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <code className="break-all rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
      {userId || '—'}
    </code>

    <button
      type="button"
      onClick={copyUserId}
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
    >
      <Copy size={16} />
      Скопировать
    </button>
  </div>
</div>
          </div>

          <div className="grid gap-4 p-8 sm:grid-cols-3">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <BookOpen size={16} /> Куплено курсов
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {studentStats?.total ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <CheckCircle size={16} /> Завершено
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {studentStats?.completed ?? 0}
              </div>
            </div>
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <TrendingUp size={16} /> Средний прогресс
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {studentStats?.avg ?? 0}%
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                  style={{ width: `${studentStats?.avg ?? 0}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-8 py-5 sm:flex-row sm:justify-end">
            <Link
                to="/courses"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-5 py-3 font-semibold text-gray-800 dark:text-gray-100 shadow-sm transition hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Sparkles size={18} />
              Найти курс
            </Link>
            <Link
                to="/my-courses"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white shadow-md transition hover:bg-indigo-700"
            >
              <BookOpen size={18} />
              Мои курсы
            </Link>
          </div>
        </div>
    );

    const adminContent = (
        <div className={`${sectionCardClass} overflow-hidden`}>
          <div className="border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-rose-50 to-orange-50 dark:from-gray-800 dark:to-gray-800 p-8">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-gray-700 px-3 py-1 text-sm font-medium text-rose-700 dark:text-rose-300 shadow-sm">
              <ShieldCheck size={16} />
              Администратор платформы
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{user?.name || '—'}</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{user?.email || '—'}</p>
          </div>

          <div className="grid gap-4 p-8 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/dashboard/admin"
              className="group rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md transition"
            >
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-2">
                <LayoutDashboard size={20} />
                <span className="text-sm font-medium">Главное</span>
              </div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">Панель админа</div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Сводка, метрики, последние события</p>
            </Link>
            <Link
              to="/dashboard/admin/course-management"
              className="group rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md transition"
            >
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-2">
                <ClipboardCheck size={20} />
                <span className="text-sm font-medium">Модерация</span>
              </div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">Курсы</div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Принять / отклонить заявки преподавателей</p>
            </Link>
            <Link
              to="/dashboard/admin/categories"
              className="group rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md transition"
            >
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-2">
                <Tag size={20} />
                <span className="text-sm font-medium">Справочники</span>
              </div>
              <div className="text-base font-semibold text-gray-900 dark:text-white">Категории</div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Создание и редактирование тем курсов</p>
            </Link>
          </div>
        </div>
    );

    const instructorContent = (
        <form onSubmit={handleSubmit} className={`${sectionCardClass} overflow-hidden`}>
          <div className="border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 p-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-gray-700 px-3 py-1 text-sm font-medium text-blue-700 dark:text-blue-300 shadow-sm">
                  <Sparkles size={16} />
                  Публичный профиль преподавателя
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Профиль инструктора</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Эта информация будет отображаться студентам на платформе.
                </p>
              </div>

              <div className="min-w-[220px] rounded-2xl bg-white dark:bg-gray-900/60 p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Заполнено</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                  {completionPercentage}%
                </span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500"
                      style={{ width: `${completionPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {isSuccess && (
                <div className="flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-4 text-green-700 dark:border-green-900 dark:bg-green-900/20 dark:text-green-300">
                  <CheckCircle size={20} />
                  <span>Профиль успешно обновлён.</span>
                </div>
            )}

            {updateError && (
                <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
                  <AlertTriangle size={20} />
                  <span>{updateError.data?.message || 'Не удалось сохранить профиль.'}</span>
                </div>
            )}

            <section className="space-y-6">
              <h3 className={iconTitleClass}>
                <UserCircle2 size={20} />
                Основная информация
              </h3>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label
                      htmlFor="publicName"
                      className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Публичное имя *
                  </label>
                  <input
                      id="publicName"
                      name="publicName"
                      type="text"
                      value={formData.publicName}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Например: Алексей Иванов"
                      required
                  />
                </div>

                <div>
                  <label
                      htmlFor="specialization"
                      className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Специализация *
                  </label>
                  <input
                      id="specialization"
                      name="specialization"
                      type="text"
                      value={formData.specialization}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Frontend-разработчик, UI/UX дизайнер и т.д."
                      required
                  />
                </div>

                <div>
                  <label
                      htmlFor="experience"
                      className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Опыт работы (лет) *
                  </label>
                  <input
                      id="experience"
                      name="experience"
                      type="number"
                      min="0"
                      max="50"
                      value={formData.experience}
                      onChange={handleChange}
                      className={inputClass}
                      required
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                      htmlFor="bio"
                      className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    О себе *
                  </label>
                  <textarea
                      id="bio"
                      name="bio"
                      rows="5"
                      maxLength="500"
                      value={formData.bio}
                      onChange={handleChange}
                      className={textareaClass}
                      placeholder="Расскажите о своём опыте, знаниях, проектах и о том, чему вы обучаете."
                      required
                  />
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Чем подробнее описание, тем выше доверие студентов.</span>
                    <span>{formData.bio.length}/500</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-gray-700 pt-8">
              <h3 className={iconTitleClass}>
                <Link2 size={20} />
                Социальные сети и ссылки
              </h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {socialFields.map((social) => (
                    <div key={social.key}>
                      <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {social.label}
                      </label>
                      <input
                          type="text"
                          value={formData.socialLinks[social.key] || ''}
                          onChange={(e) => handleSocialLinkChange(social.key, e.target.value)}
                          className={inputClass}
                          placeholder={social.placeholder}
                      />
                    </div>
                ))}
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-gray-700 pt-8">
              <h3 className={iconTitleClass}>
                <GraduationCap size={20} />
                Образование
              </h3>

              <div className={softCardClass}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                      type="text"
                      placeholder="Учебное заведение *"
                      value={newEducation.institution}
                      onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="text"
                      placeholder="Степень * (Бакалавр, Магистр и т.д.)"
                      value={newEducation.degree}
                      onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="text"
                      placeholder="Специальность"
                      value={newEducation.field}
                      onChange={(e) => setNewEducation({ ...newEducation, field: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="number"
                      placeholder="Год окончания"
                      value={newEducation.year}
                      onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })}
                      className={inputClass}
                  />
                </div>

                <button
                    type="button"
                    onClick={addEducation}
                    disabled={!newEducation.institution || !newEducation.degree}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus size={16} />
                  Добавить образование
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {formData.education.length > 0 ? (
                    formData.education.map((edu, index) => (
                        <div key={index} className={itemCardClass}>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">{edu.institution}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {edu.degree}
                              {edu.field ? ` — ${edu.field}` : ''}
                            </p>
                            {edu.year && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{edu.year} год</p>
                            )}
                          </div>
                          <button
                              type="button"
                              onClick={() => removeEducation(index)}
                              className={removeBtnClass}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                    ))
                ) : (
                    <p className="rounded-xl bg-gray-50 dark:bg-gray-700/40 py-4 text-center text-gray-500 dark:text-gray-400">
                      Образование пока не добавлено
                    </p>
                )}
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-gray-700 pt-8">
              <h3 className={iconTitleClass}>
                <Briefcase size={20} />
                Опыт работы
              </h3>

              <div className={softCardClass}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                      type="text"
                      placeholder="Должность *"
                      value={newWork.position}
                      onChange={(e) => setNewWork({ ...newWork, position: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="text"
                      placeholder="Компания *"
                      value={newWork.company}
                      onChange={(e) => setNewWork({ ...newWork, company: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="number"
                      placeholder="Год начала"
                      value={newWork.startYear}
                      onChange={(e) => setNewWork({ ...newWork, startYear: e.target.value })}
                      className={inputClass}
                  />
                  <div className="flex items-center gap-3">
                    <input
                        type="number"
                        placeholder="Год окончания"
                        value={newWork.endYear}
                        onChange={(e) => setNewWork({ ...newWork, endYear: e.target.value })}
                        className={`${inputClass} flex-1`}
                        disabled={newWork.current}
                    />
                    <label className="flex items-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <input
                          type="checkbox"
                          checked={newWork.current}
                          onChange={(e) => setNewWork({ ...newWork, current: e.target.checked })}
                      />
                      Сейчас работаю
                    </label>
                  </div>
                </div>

                <textarea
                    placeholder="Описание обязанностей, достижений или проектов"
                    value={newWork.description}
                    onChange={(e) => setNewWork({ ...newWork, description: e.target.value })}
                    rows="3"
                    className={`${textareaClass} mt-4`}
                />

                <button
                    type="button"
                    onClick={addWorkExperience}
                    disabled={!newWork.position || !newWork.company}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus size={16} />
                  Добавить опыт
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {formData.workExperience.length > 0 ? (
                    formData.workExperience.map((work, index) => (
                        <div key={index} className={itemCardClass}>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{work.position}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{work.company}</p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {work.startYear || '—'} — {work.current ? 'по настоящее время' : work.endYear || '—'}
                            </p>
                            {work.description && (
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                  {work.description}
                                </p>
                            )}
                          </div>
                          <button
                              type="button"
                              onClick={() => removeWorkExperience(index)}
                              className={removeBtnClass}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                    ))
                ) : (
                    <p className="rounded-xl bg-gray-50 dark:bg-gray-700/40 py-4 text-center text-gray-500 dark:text-gray-400">
                      Опыт работы пока не добавлен
                    </p>
                )}
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-gray-700 pt-8">
              <h3 className={iconTitleClass}>
                <Award size={20} />
                Достижения
              </h3>

              <div className={softCardClass}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                      type="text"
                      placeholder="Название достижения *"
                      value={newAchievement.title}
                      onChange={(e) => setNewAchievement({ ...newAchievement, title: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="number"
                      placeholder="Год"
                      value={newAchievement.year}
                      onChange={(e) => setNewAchievement({ ...newAchievement, year: e.target.value })}
                      className={inputClass}
                  />
                  <input
                      type="text"
                      placeholder="Кем выдано"
                      value={newAchievement.issuer}
                      onChange={(e) => setNewAchievement({ ...newAchievement, issuer: e.target.value })}
                      className="md:col-span-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/70 px-4 py-3 text-gray-900 dark:text-white shadow-sm outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>

                <textarea
                    placeholder="Краткое описание достижения"
                    value={newAchievement.description}
                    onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                    rows="3"
                    className={`${textareaClass} mt-4`}
                />

                <button
                    type="button"
                    onClick={addAchievement}
                    disabled={!newAchievement.title}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus size={16} />
                  Добавить достижение
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {formData.achievements.length > 0 ? (
                    formData.achievements.map((achievement, index) => (
                        <div key={index} className={itemCardClass}>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{achievement.title}</h4>
                            {achievement.issuer && (
                                <p className="text-sm text-gray-600 dark:text-gray-300">{achievement.issuer}</p>
                            )}
                            {achievement.year && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  {achievement.year} год
                                </p>
                            )}
                            {achievement.description && (
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                  {achievement.description}
                                </p>
                            )}
                          </div>
                          <button
                              type="button"
                              onClick={() => removeAchievement(index)}
                              className={removeBtnClass}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                    ))
                ) : (
                    <p className="rounded-xl bg-gray-50 dark:bg-gray-700/40 py-4 text-center text-gray-500 dark:text-gray-400">
                      Достижения пока не добавлены
                    </p>
                )}
              </div>
            </section>

            <section className="border-t border-gray-100 dark:border-gray-700 pt-8">
              <h3 className={iconTitleClass}>
                <Wallet size={20} />
                Контакт с командой Uaqyt
              </h3>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label
                      htmlFor="kaspiPhone"
                      className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Номер WhatsApp для связи
                  </label>
                  <input
                      id="kaspiPhone"
                      name="kaspiPhone"
                      type="text"
                      value={formData.kaspiPhone}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="+7 777 123 45 67"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Менеджер Uaqyt свяжется с вами по WhatsApp на этот номер —
                    например, чтобы согласовать выплату или уточнить вопрос по курсу.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">Поля, отмеченные *, обязательны.</p>

            <button
                type="submit"
                disabled={isUpdating || isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUpdating ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Сохранение...
                  </>
              ) : (
                  <>
                    <Save size={18} />
                    Сохранить профиль
                  </>
              )}
            </button>
          </div>
        </form>
    );

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Профиль</h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Управляйте данными аккаунта и настройками публичного профиля.
            </p>
          </div>

          <div className="flex flex-col gap-8 xl:flex-row">
            <aside className="w-full xl:w-80">
              <div className="sticky top-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
                <AvatarUpload />
              </div>
            </aside>

            <main className="flex-1">
              {isLoading && isInstructor ? (
                  <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <Loader className="animate-spin text-blue-500" size={32} />
                  </div>
              ) : isAdmin ? (
                  adminContent
              ) : isInstructor ? (
                  instructorContent
              ) : (
                  studentContent
              )}
            </main>
          </div>
        </div>
    );
  };

  export default ProfilePage;